import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { createInstitutionApi, isInstitutionStaff } from '../lib/institutionApi';
import { INSTITUTIONS_ENABLED } from '../lib/featureFlags';

const InstitutionContext = createContext(null);
const MEMBERSHIP_CACHE_KEY = 'ucat_memberships_cache';

function readMembershipCache(token) {
  if (!token) return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(MEMBERSHIP_CACHE_KEY) || 'null');
    if (!parsed || parsed.token !== token || !Array.isArray(parsed.memberships)) return null;
    return parsed.memberships;
  } catch {
    return null;
  }
}

function writeMembershipCache(token, memberships) {
  try {
    if (!token) sessionStorage.removeItem(MEMBERSHIP_CACHE_KEY);
    else sessionStorage.setItem(MEMBERSHIP_CACHE_KEY, JSON.stringify({ token, memberships }));
  } catch {
    /* private mode / quota */
  }
}

function pickActiveInstitutionId(memberships) {
  const staff = (memberships || []).filter((m) => isInstitutionStaff(m.member_role));
  if (staff.length === 0) return null;
  const stored = localStorage.getItem('active_institution_id');
  const valid = staff.find((m) => m.institution.id === stored);
  return valid ? stored : staff[0].institution.id;
}

export const InstitutionProvider = ({ children }) => {
  const { api, token } = useAuth();
  const cached = useMemo(() => (INSTITUTIONS_ENABLED ? readMembershipCache(token) : null), [token]);
  const [allMemberships, setAllMemberships] = useState(cached || []);
  const [activeInstitutionId, setActiveInstitutionId] = useState(() =>
    pickActiveInstitutionId(INSTITUTIONS_ENABLED ? readMembershipCache(localStorage.getItem('ucat_token')) : []),
  );
  const [loading, setLoading] = useState(Boolean(INSTITUTIONS_ENABLED && token && !cached));

  const institutionApi = useMemo(() => (api ? createInstitutionApi(api) : null), [api]);

  const staffMemberships = useMemo(
    () => allMemberships.filter((m) => isInstitutionStaff(m.member_role)),
    [allMemberships],
  );

  const studentMemberships = useMemo(
    () => allMemberships.filter((m) => m.member_role === 'student'),
    [allMemberships],
  );

  const hasInstitutionPlatformAccess = useMemo(
    () => INSTITUTIONS_ENABLED && studentMemberships.some(
      (m) => ['trial', 'active'].includes(m.institution?.status) && m.platform_access,
    ),
    [studentMemberships],
  );

  const hasInstitutionPortalAccess = useMemo(
    () => INSTITUTIONS_ENABLED && studentMemberships.some(
      (m) => m.member_role === 'student'
        && m.member_status === 'active'
        && ['trial', 'active'].includes(m.institution?.status),
    ),
    [studentMemberships],
  );

  const hasInstitutionStudentAccess = hasInstitutionPlatformAccess;

  const applyMemberships = useCallback((list) => {
    const next = list || [];
    setAllMemberships(next);
    writeMembershipCache(token, next);
    const staff = next.filter((m) => isInstitutionStaff(m.member_role));
    if (staff.length > 0) {
      const stored = localStorage.getItem('active_institution_id');
      const valid = staff.find((m) => m.institution.id === stored);
      setActiveInstitutionId(valid ? stored : staff[0].institution.id);
    } else {
      setActiveInstitutionId(null);
    }
  }, [token]);

  const fetchMemberships = useCallback(async () => {
    if (!INSTITUTIONS_ENABLED || !token || !institutionApi) {
      setAllMemberships([]);
      writeMembershipCache(null, []);
      setLoading(false);
      return;
    }
    if (!readMembershipCache(token)) setLoading(true);
    try {
      const res = await institutionApi.getMyMemberships();
      applyMemberships(res.data || []);
    } catch (err) {
      console.error('Failed to load institution memberships:', err);
    } finally {
      setLoading(false);
    }
  }, [token, institutionApi, applyMemberships]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const setActiveInstitution = useCallback((id) => {
    setActiveInstitutionId(id);
    if (id) localStorage.setItem('active_institution_id', id);
    else localStorage.removeItem('active_institution_id');
  }, []);

  const activeMembership = staffMemberships.find((m) => m.institution.id === activeInstitutionId) || null;
  const activeInstitution = activeMembership?.institution || null;
  const memberRole = activeMembership?.member_role || null;

  const value = useMemo(() => ({
    memberships: staffMemberships,
    allMemberships,
    studentMemberships,
    hasInstitutionStudentAccess,
    hasInstitutionPlatformAccess,
    hasInstitutionPortalAccess,
    activeInstitution,
    activeInstitutionId,
    memberRole,
    isInstitutionStaff: isInstitutionStaff(memberRole),
    institutionApi,
    loading,
    setActiveInstitution,
    refreshMemberships: fetchMemberships,
  }), [
    staffMemberships,
    allMemberships,
    studentMemberships,
    hasInstitutionStudentAccess,
    hasInstitutionPlatformAccess,
    hasInstitutionPortalAccess,
    activeInstitution,
    activeInstitutionId,
    memberRole,
    institutionApi,
    loading,
    setActiveInstitution,
    fetchMemberships,
  ]);

  return (
    <InstitutionContext.Provider value={value}>
      {children}
    </InstitutionContext.Provider>
  );
};

export const useInstitution = () => {
  const ctx = useContext(InstitutionContext);
  if (!ctx) throw new Error('useInstitution must be used within InstitutionProvider');
  return ctx;
};
